/**
 * Complete Account Form Component
 * 
 * Form to collect remaining user details for guest accounts.
 * Shown after payment method is added for new users.
 * 
 * @module pages/booking/components
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { EyeIcon } from '@butter/ui/icons';
import { useAuth } from '~/services/auth';
import type { User } from '~/services/auth/types';
import { enableInternalTools } from '~/config/env';
import { getPasswordStrength, validateUserPassword } from '~/lib/utils/passwordValidation';
import { isValidPhone, normalizePhone, type PhoneRegion } from '@butter/library/phone';
import { isPlaceholderEmail } from '@butter/library/shared';

export type CompleteAccountFormProps = (
  | {
      /** Default mode: finish an EXISTING incomplete/pending account. */
      mode?: 'complete';
      /** User ID of the guest account */
      userId: string | number;
      /** User's email (editable, already captured) */
      email: string;
      /**
       * Fired after the account is completed and signed in, with the completed user. The
       * caller adopts it in place of the incomplete guest it started from — otherwise a
       * retry on the same step submits this form again for an account that no longer
       * needs it, and the server answers "User not found".
       */
      onComplete: (user: User) => void;
      /** Optional club ID */
      clubId?: string | number;
    }
  | {
      /**
       * Create a brand-new account in one shot via POST /supabase-auth/register
       * (active status + home enrollment + live session — no incomplete user,
       * no follow-up login). The demo flow's path (S3): demos never create
       * incomplete users, that machinery exists only to capture a payment
       * method early.
       */
      mode: 'create';
      /** Club the account registers under (becomes their home club). */
      clubId: number;
      /**
       * Fired AFTER register succeeded — the session is already live, and the
       * registered user is passed so the caller can act on it without racing
       * the auth context re-render.
       */
      onComplete: (user: { id: string | number }) => void;
      /**
       * The entered email already has an account — at this tenant (409
       * EMAIL_IN_USE_*) or anywhere else (409 EXISTING_ACCOUNT_PASSWORD_REQUIRED,
       * the Mode-1 link case). The parent hands off to the shared sign-in
       * modal, whose existing handling covers every branch from there:
       * ACCOUNT_SETUP_INCOMPLETE routes to /account-setup, an other-tenant
       * account gets the "go to your club" guidance. No inline link-password
       * retry — this form's strength validation would dead-end an older
       * account's existing password (PR #1375 nit 11).
       */
      onAccountExists: (email: string) => void;
    }) & {
  /** The club's country — how a number typed without a country code is read. Defaults to US. */
  phoneRegion?: PhoneRegion;
};

interface FormData {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

/**
 * Complete Account Form Component
 * 
 * Collects:
 * - First Name (required)
 * - Last Name (required)
 * - Phone Number (required)
 * - Password (required, with confirmation)
 * 
 * Default (`complete`) mode calls `/supabase-auth/complete-account-setup`:
 * updates the existing user's details, sets the password, and activates the
 * account. `create` mode (demo flow) calls `/supabase-auth/register` instead —
 * no pre-existing user required.
 */
export function CompleteAccountForm(props: CompleteAccountFormProps) {
  const isCreateMode = props.mode === 'create';
  const phoneRegion = props.phoneRegion ?? 'US';
  const { login, register } = useAuth();
  const isInternal = enableInternalTools();

  const [formData, setFormData] = useState<FormData>({
    email: isCreateMode ? '' : props.email,
    firstName: isInternal ? 'Test' : '',
    lastName: isInternal ? 'User' : '',
    // `555-01XX` fictional block. `5555555555` is not a valid NANP number, so once the API
    // started validating phones this prefill 400'd the whole flow — and `enableInternalTools()`
    // is dev OR staging, so it broke both.
    phone: isInternal ? '+12025550142' : '',
    password: isInternal ? 'Password123!' : '',
    confirmPassword: isInternal ? 'Password123!' : '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Visual strength meter (shared) — the pass/fail gate is validateUserPassword below.
  const passwordStrength = getPasswordStrength(formData.password);

  // Form validation
  const validateForm = (): string | null => {
    if (!formData.email.trim()) {
      return 'Email is required';
    }
    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      return 'Please enter a valid email address';
    }
    // A migrated member arrives with a generated `noemail.*@buttersoftware.temp` address. It is
    // syntactically valid, so the regex above passes it and the field prefills with it — but
    // `completeAccountSetup` rejects it server-side, which surfaced as an unexplained failure
    // with nothing marked on the form. Mirrors the check AccountSetupForm already does.
    if (isPlaceholderEmail(formData.email)) {
      return 'Please enter your real email address';
    }
    if (!formData.firstName.trim()) {
      return 'First name is required';
    }
    if (!formData.lastName.trim()) {
      return 'Last name is required';
    }
    if (!formData.phone.trim()) {
      return 'Phone number is required';
    }
    // Was `/^\+?[\d\s\-()]+$/`, which did not even require a digit — `"()"` passed. The API
    // now normalizes to E.164 or 400s, so anything weaker than its rule turns an inline error
    // into a round-trip failure.
    if (!isValidPhone(formData.phone, phoneRegion)) {
      return 'Please enter a valid phone number';
    }
    const pwResult = validateUserPassword(formData.password);
    if (!pwResult.isValid) {
      return pwResult.errors[0];
    }
    if (formData.password !== formData.confirmPassword) {
      return 'Passwords do not match';
    }
    return null;
  };

  // Handle input changes
  const handleChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    // Normalized client-side so what we send matches what the server stores; the server
    // normalizes again and remains the authority. `validateForm` already rejected anything
    // unparseable, so the fallback is unreachable — it exists because the result is a union.
    const normalizedPhone = normalizePhone(formData.phone, phoneRegion);

    try {
      setIsLoading(true);
      setError(null);

      if (props.mode === 'create') {
        // One shot: active user + home enrollment + hydrated session. No
        // follow-up login() — register() stores the session itself.
        const registeredUser = await register({
          email: formData.email.trim(),
          password: formData.password,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          phone: normalizedPhone.ok ? normalizedPhone.e164 : undefined,
          clubId: props.clubId,
        });
        props.onComplete(registeredUser);
        return;
      }

      // Import auth service
      const { authService } = await import('~/services/auth');

      /**
       * No `setupToken` argument — deliberate, and load-bearing.
       *
       * This form serves BOTH `incomplete` members (self-created seconds ago in this
       * flow) and `pending` members (admin-invited, arriving mid-booking). The booking
       * flow has no channel through which an invitation token could reach it: mobile
       * *discovers* a web-created incomplete user via an unauthenticated lookup, so no
       * token can be handed over at create-time.
       *
       * `incomplete` was never token-gated (the G8 booking half). `pending` only
       * succeeds here while the TEMPORARY G23 window is open in the API. If a `pending`
       * member reports "A valid setup link is required to finish setting up your
       * account" from inside a booking, G23 has been reverted and THIS call site is
       * what broke — not their invitation.
       *
       * @see /apps/api/src/services/auth/docs/KNOWN_GAPS.md G23, G8
       */
      const response = await authService.completeAccountSetup(
        props.userId,
        {
          email: formData.email.trim(),
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          phone: normalizedPhone.ok ? normalizedPhone.e164 : '',
          password: formData.password,
        },
        props.clubId
      );

      console.log('[CompleteAccountForm] Account setup complete:', response.user);

      // Log in the user with their new credentials to establish the session
      await login(formData.email.trim(), formData.password);

      // Success - hand the completed user to the parent.
      props.onComplete(response.user);
    } catch (err) {
      console.error('[CompleteAccountForm] Setup failed:', err);

      if (props.mode === 'create') {
        const code = (err as Error & { code?: string })?.code;
        if (code?.startsWith('EMAIL_IN_USE') || code === 'EXISTING_ACCOUNT_PASSWORD_REQUIRED') {
          // The email already has an account somewhere — hand off to sign-in
          // (see onAccountExists). What they typed rides along as the prefill.
          props.onAccountExists(formData.email.trim());
          return;
        }
      }

      setError(
        err instanceof Error
          ? err.message
          : 'Failed to complete account setup. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white">
      <form id="complete-account-form" onSubmit={handleSubmit} className="space-y-4">
        {/* Error Message */}
        {error && (
          <div 
          className="p-4 bg-red-50 border border-red-200 rounded-lg" role="alert" aria-live="polite"
          data-testid="confirmation-form-error-message">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Email (Editable) */}
        <div className="text-sm">
          <label 
            htmlFor="email"
            className="block font-medium text-fg-quaternary mb-2"
            data-testid="confirmation-form-email-label"
          >
            Email Address <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            disabled={isLoading}
            required
            placeholder="you@example.com"
            data-testid="confirmation-form-email-input"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-500 focus:border-transparent disabled:bg-gray-50"
          />
        </div>

        {/* First Name */}
        <div className="text-sm">
          <label 
            htmlFor="firstName" 
            className="block text-sm font-medium text-fg-quaternary mb-2"
            data-testid="confirmation-form-firstName-label"
          >
            First Name <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id="firstName"
            type="text"
            value={formData.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            disabled={isLoading}
            required
            placeholder="John"
            data-testid="confirmation-form-firstName-input"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-500 focus:border-transparent disabled:bg-gray-50"
          />
        </div>

        {/* Last Name */}
        <div className="text-sm">
          <label 
            htmlFor="lastName" 
            className="block text-sm font-medium text-fg-quaternary mb-2"
            data-testid="confirmation-form-lastName-label"
          >
            Last Name <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id="lastName"
            type="text"
            data-testid="confirmation-form-lastName-input"
            value={formData.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            disabled={isLoading}
            required
            placeholder="Doe"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-500 focus:border-transparent disabled:bg-gray-50"
          />
        </div>

        {/* Phone (Required) */}
        <div className="text-sm">
          <label 
            htmlFor="phone" 
            className="block text-sm font-medium text-fg-quaternary mb-2"
            data-testid="confirmation-form-phone-label"
          >
            Phone Number <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            disabled={isLoading}
            required
            data-testid="confirmation-form-phone-input"
            placeholder="+1 (555) 123-4567"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-500 focus:border-transparent disabled:bg-gray-50"
          />
        </div>

        {/* Password */}
        <div className="text-sm">
          <label 
            htmlFor="password" 
            className="block text-sm font-medium text-fg-quaternary mb-2"
            data-testid="confirmation-form-password-label"
          >
            Password <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              disabled={isLoading}
              data-testid="confirmation-form-password-input"
              required
              minLength={8}
              placeholder="Min. 8 characters"
              className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-500 focus:border-transparent disabled:bg-gray-50"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100 transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <EyeIcon 
                isOpen={showPassword}
                className="size-6 text-gray-500"
                ariaHidden={true}
              />
            </button>
          </div>
          
          {/* Password Strength Indicator */}
          {formData.password && (
            <div className="mt-2">
              <div className="flex gap-1">
                <div className={`h-1 flex-1 rounded ${
                  passwordStrength.strength === 'weak' ? 'bg-red-500' :
                  passwordStrength.strength === 'medium' ? 'bg-yellow-500' :
                  'bg-green-500'
                }`} />
                <div className={`h-1 flex-1 rounded ${
                  passwordStrength.strength === 'medium' || passwordStrength.strength === 'strong' 
                    ? passwordStrength.strength === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                    : 'bg-gray-200'
                }`} />
                <div className={`h-1 flex-1 rounded ${
                  passwordStrength.strength === 'strong' ? 'bg-green-500' : 'bg-gray-200'
                }`} />
              </div>
              <p className={`mt-1 text-xs ${
                passwordStrength.strength === 'weak' ? 'text-red-600' :
                passwordStrength.strength === 'medium' ? 'text-yellow-600' :
                'text-green-600'
              }`}>
                {passwordStrength.message}
              </p>
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div className="text-sm">
          <label 
            htmlFor="confirmPassword" 
            className="block font-medium text-gray-700 mb-2"
            data-testid="confirmation-form-confirmPassword-label"
          >
            Confirm Password <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => handleChange('confirmPassword', e.target.value)}
              disabled={isLoading}
              required
              placeholder="Re-enter password"
              data-testid="confirmation-form-confirmPassword-input"
              className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-500 focus:border-transparent disabled:bg-gray-50"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100 transition-colors"
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              <EyeIcon 
                isOpen={showConfirmPassword}
                className="size-6 text-gray-500"
                ariaHidden={true}
              />
            </button>
          </div>
          {formData.confirmPassword && formData.password !== formData.confirmPassword && (
            <p className="mt-1 text-xs text-red-600">
              Passwords do not match
            </p>
          )}
        </div>

        {/* Submit Button - Hidden (controlled externally) */}
        <button
          type="submit"
          className="hidden"
          aria-hidden="true"
        >
          Submit
        </button>
      </form>
    </div>
  );
}
